import { useCallback, useId, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Group from 'components/utils/Group'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { LeCruiseSavedItemData, ManualItineraryDeleteRequest, TransportConnection, TransportOption } from 'tripPlanner/api/types'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { CANVAS_CONTENT_WIDTH } from './constants'
import { getTransportIcon } from './TransportOptionItem'
import ChatCruiseTransportOfferCard from './ChatCruiseTransportOfferCard'
import ChatCruiseTransportScheduleCard from './ChatCruiseTransportScheduleCard'
import {
  formatCruiseDate,
  formatCruiseDuration,
  formatScheduleDuration,
  getCruiseRouteName,
  getCruiseScheduleDetails,
  getCruiseScheduleMapPoints,
} from '../utils/cruiseTransportUtils'

const messages = defineMessages({
  cruiseTransportLabel: {
    id: 'GGHLKo3I0NAxKBk9bKd_',
    defaultMessage: 'Cruise',
    description: 'Transport mode label shown for cruise transport legs in the trip itinerary',
  },
  metaWithoutDuration: {
    id: '8teclgZzpK4dw3Z9IW7n',
    defaultMessage: '{transportType}{separator}{cruiseLineName}',
    description: 'Summary metadata shown under a cruise transport route when duration is unavailable',
  },
  metaWithDuration: {
    id: '7De2fWVuGsacMlWKirnd',
    defaultMessage: '{transportType}{separator}{cruiseLineName}{separator}{duration}',
    description: 'Summary metadata shown under a cruise transport route in the trip itinerary',
  },
  detailWithDuration: {
    id: 'w6VOGvbxEf0PhdiiWugW',
    defaultMessage: '{cruiseLineName}{separator}{duration}',
    description: 'Cruise offer metadata showing cruise line and duration in the transport card',
  },
  deleteCruise: {
    id: '3YG6URy46p8bDk35gpsB',
    defaultMessage: 'Delete',
    description: 'Button label to delete a cruise transport item from the trip itinerary',
  },
  viewOffer: {
    id: 'leJh3S1VsjVpkR9P95hA',
    defaultMessage: 'View offer',
    description: 'Button label that opens cruise offer details from the trip itinerary',
  },
  viewOnMap: {
    id: '8mQFJsxj2JKFgnzx8Co9',
    defaultMessage: 'View on map',
    description: 'Map button label for viewing cruise transport ports from the itinerary schedule card',
  },
})

// This section draws no chrome of its own, but still needs the surface's inline inset
// when the canvas renders sections edge to edge (the mobile timeline).
const TransportContainer = styled.div`
  max-width: var(--itinerary-canvas-width, ${rem(CANVAS_CONTENT_WIDTH)});
  width: 100%;
  padding-inline: var(--trip-plan-canvas-section-padding, 0);
`

const SummaryButton = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: ${rem(20)};
  padding: 0;
  width: 100%;
  text-align: start;
  cursor: pointer;
`

const IconBadge = styled.div`
  width: ${rem(32)};
  height: ${rem(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow:
    0 ${rem(0.7)} ${rem(1.4)} rgba(0, 0, 0, 0.07),
    0 ${rem(1.9)} ${rem(4)} rgba(0, 0, 0, 0.05),
    0 ${rem(4.5)} ${rem(10)} rgba(0, 0, 0, 0.05);
`

const SummaryContent = styled(Group)`
  min-width: 0;
  flex: 1;
  padding-inline-end: ${rem(20)};
`

const SummaryText = styled.div`
  min-width: 0;
  flex: 1;
`

const ExpandedContent = styled(Group)`
  margin-top: ${rem(16)};
`

interface Props {
  transport: TransportConnection
  option: TransportOption
  savedItemData: LeCruiseSavedItemData
  onDeleteTarget?: (target: ManualItineraryDeleteRequest) => Promise<void> | void
}

function ChatCruiseTransportLine({ transport, option, savedItemData, onDeleteTarget }: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { isManualItineraryDeletePending } = useItineraryContext()
  const detailsId = useId()
  const [isExpanded, setIsExpanded] = useState(true)

  const cruiseTypeLabel = intl.formatMessage(messages.cruiseTransportLabel)
  const middleDotSeparator = formatMiddleDotSeparator(intl)
  const deleteActionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending

  const routeName = useMemo(
    () => getCruiseRouteName(transport, savedItemData, cruiseTypeLabel, intl),
    [cruiseTypeLabel, intl, savedItemData, transport],
  )

  const durationLabel = useMemo(
    () => formatCruiseDuration(option, savedItemData, intl),
    [intl, option, savedItemData],
  )

  const cruiseLineLabel = savedItemData.cruiseLineName ?? savedItemData.shipName ?? cruiseTypeLabel

  const metaLabel = useMemo(() => {
    if (!durationLabel) {
      return intl.formatMessage(messages.metaWithoutDuration, {
        transportType: cruiseTypeLabel,
        separator: middleDotSeparator,
        cruiseLineName: cruiseLineLabel,
      })
    }
    return intl.formatMessage(messages.metaWithDuration, {
      transportType: cruiseTypeLabel,
      separator: middleDotSeparator,
      cruiseLineName: cruiseLineLabel,
      duration: durationLabel,
    })
  }, [cruiseLineLabel, cruiseTypeLabel, durationLabel, intl, middleDotSeparator])

  const detailLabel = useMemo(() => {
    if (!durationLabel) return cruiseLineLabel
    return intl.formatMessage(messages.detailWithDuration, {
      cruiseLineName: cruiseLineLabel,
      separator: middleDotSeparator,
      duration: durationLabel,
    })
  }, [cruiseLineLabel, durationLabel, intl, middleDotSeparator])

  const shipName = savedItemData.shipName ?? cruiseLineLabel
  const scheduleDetails = useMemo(
    () => getCruiseScheduleDetails(option),
    [option],
  )
  const scheduleDurationLabel = scheduleDetails ? formatScheduleDuration(scheduleDetails, intl) : undefined
  const scheduleDepartureDate = formatCruiseDate(scheduleDetails?.departure.localDate, intl)
  const scheduleArrivalDate = formatCruiseDate(scheduleDetails?.arrival.localDate, intl)
  const scheduleMapPoints = useMemo(
    () => getCruiseScheduleMapPoints(scheduleDetails),
    [scheduleDetails],
  )

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])

  const openOfferDrawer = useCallback(() => {
    if (!savedItemData.offerId) return
    eventBus.dispatch({
      type: 'open_offer_drawer',
      offerId: savedItemData.offerId,
      offerType: 'cruise',
    })
  }, [eventBus, savedItemData.offerId])

  const showScheduleOnMap = useCallback(() => {
    if (scheduleMapPoints.length === 0) return
    eventBus.dispatch({ type: 'show_cruise_transport_on_map', points: scheduleMapPoints })
  }, [eventBus, scheduleMapPoints])

  const deleteCruise = useCallback(() => {
    if (deleteActionsDisabled) return
    onDeleteTarget?.({ targetType: 'item', itemId: option.itemId })
  }, [deleteActionsDisabled, onDeleteTarget, option.itemId])

  const mapLabel = intl.formatMessage(messages.viewOnMap)
  const deleteLabel = intl.formatMessage(messages.deleteCruise)
  const viewOfferLabel = intl.formatMessage(messages.viewOffer)
  const ExpansionIcon = isExpanded ? LineAngleUpIcon : LineAngleDownIcon
  const scheduleMapClick = scheduleMapPoints.length > 0 ? showScheduleOnMap : undefined
  const deleteAction = onDeleteTarget ? deleteCruise : undefined
  const transportIcon = getTransportIcon(option.type, 'S')

  return (
    <TransportContainer>
      <SummaryButton
        type="button"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? detailsId : undefined}
      >
        <IconBadge aria-hidden>
          {transportIcon}
        </IconBadge>
        <SummaryContent direction="horizontal" gap={20} verticalAlign="center">
          <SummaryText>
            <Heading variant="heading5" as="div">{routeName}</Heading>
            <BodyText variant="small">{metaLabel}</BodyText>
          </SummaryText>
          <ExpansionIcon size="M" />
        </SummaryContent>
      </SummaryButton>

      {isExpanded && (
        <ExpandedContent id={detailsId} direction="vertical" gap={16}>
          <ChatCruiseTransportOfferCard
            savedItemData={savedItemData}
            transportType={option.type}
            shipName={shipName}
            detailLabel={detailLabel}
            deleteLabel={deleteLabel}
            viewOfferLabel={viewOfferLabel}
            deleteActionsDisabled={deleteActionsDisabled}
            onDelete={deleteAction}
            onViewOffer={openOfferDrawer}
          />

          {scheduleDetails && (
            <ChatCruiseTransportScheduleCard
              scheduleDetails={scheduleDetails}
              scheduleDurationLabel={scheduleDurationLabel}
              scheduleDepartureDate={scheduleDepartureDate}
              scheduleArrivalDate={scheduleArrivalDate}
              mapLabel={mapLabel}
              isMapDisabled={scheduleMapPoints.length === 0}
              onMapClick={scheduleMapClick}
            />
          )}
        </ExpandedContent>
      )}
    </TransportContainer>
  )
}

export default ChatCruiseTransportLine
